import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Trophy, ArrowUpDown, User, Crown, Search, Filter, ArrowRight } from 'lucide-react';
import { RatingPill, RATING_STYLE, fmtCompact } from './people/DeepDiveSections';
import { WorkloadChip, WORKLOAD_DISCLAIMER } from './people/CapacityChip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_KEYS = [
  { key: 'rating',           label: 'Rating',           type: 'rating' },
  { key: 'subject',          label: 'Name',             type: 'string' },
  { key: 'subjectType',      label: 'Type',             type: 'string' },
  { key: 'planCount',        label: 'Plans',            type: 'number' },
  { key: 'totalLots',        label: 'Lots',             type: 'number' },
  { key: 'totalRevenue',     label: 'Revenue',          type: 'number' },
  { key: 'pctUnderContract', label: 'Under Contract %', type: 'number' },
  { key: 'revenueAtRisk',    label: 'Revenue at Risk',  type: 'number' },
  { key: 'avgComplianceScore', label: 'Compliance',     type: 'number' },
  { key: 'ratedSevenPlusPct', label: 'NPS 7+%',         type: 'number' },
  { key: 'enps',             label: 'eNPS',             type: 'number' },
  { key: 'capacity',         label: 'Tracked Load',     type: 'workload' },
  { key: 'everperform',      label: 'Everperform',      type: 'pulse' },
];

const RATING_ORDER = { outstanding: 4, exceeds: 3, meets: 2, below: 1, pending: 0 };

const PerformanceLadder = ({ onOpenSubject }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | Manager | Team Leader
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sortKey, setSortKey] = useState('rating');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/performance-ladder`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = [...data.rows];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        r.subject.toLowerCase().includes(q) ||
        (r.teamLeader || '').toLowerCase().includes(q)
      );
    }
    if (typeFilter !== 'all') rows = rows.filter(r => r.subjectType === typeFilter);
    if (ratingFilter !== 'all') rows = rows.filter(r => (r.overall.ratingKey || 'pending') === ratingFilter);

    const cmp = (a, b) => {
      let av, bv;
      if (sortKey === 'rating') {
        av = RATING_ORDER[a.overall.ratingKey || 'pending'];
        bv = RATING_ORDER[b.overall.ratingKey || 'pending'];
      } else if (sortKey === 'capacity') {
        av = a.trackedVsTeamAvgPct;
        bv = b.trackedVsTeamAvgPct;
      } else if (sortKey === 'everperform') {
        av = a.everperform?.compositeScore;
        bv = b.everperform?.compositeScore;
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    };
    rows.sort(cmp);
    return rows;
  }, [data, search, typeFilter, ratingFilter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (k === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'subject' || k === 'subjectType' ? 'asc' : 'desc'); }
  };

  const openDeepDive = (row) => {
    if (!onOpenSubject) return;
    onOpenSubject(row.subjectType, row.subject);
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="ladder-loading">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  const totals = data.summary.ratingCounts;

  return (
    <div data-testid="performance-ladder" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white rounded-[14px] p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-white/10 flex items-center justify-center">
              <Trophy className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-60 mb-1">Single ladder view</p>
              <h2 className="text-xl font-bold font-heading">Performance Ladder</h2>
              <p className="text-[11px] opacity-70 mt-1 max-w-2xl leading-relaxed">
                Every OC Manager and Team Leader rated against the four pillars. Click a row to open their Deep Dive.
              </p>
              <p className="text-[10px] opacity-60 mt-2 max-w-2xl leading-relaxed" data-testid="ladder-disclaimer">
                <span className="font-semibold text-amber-200">Tracked Load</span> compares tracked hrs/week (emails · meetings · calls) to the team average — <em>not</em> total workload. {WORKLOAD_DISCLAIMER}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <SummaryChip label="Outstanding" value={totals.outstanding} keyName="outstanding" />
            <SummaryChip label="Exceeds" value={totals.exceeds} keyName="exceeds" />
            <SummaryChip label="Meets" value={totals.meets} keyName="meets" />
            <SummaryChip label="Below" value={totals.below} keyName="below" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" strokeWidth={2} />
          <input
            data-testid="ladder-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or team leader…"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-[8px] border border-[#E5E5E5] focus:outline-none focus:border-[#171717]"
          />
        </div>
        <Selector label="Type" value={typeFilter} onChange={setTypeFilter} options={[
          { value: 'all', label: 'All' },
          { value: 'Manager', label: 'Managers' },
          { value: 'Team Leader', label: 'Team Leaders' },
        ]} testId="ladder-type-filter" />
        <Selector label="Rating" value={ratingFilter} onChange={setRatingFilter} options={[
          { value: 'all', label: 'All' },
          { value: 'outstanding', label: 'Outstanding' },
          { value: 'exceeds', label: 'Exceeds' },
          { value: 'meets', label: 'Meets' },
          { value: 'below', label: 'Below' },
          { value: 'pending', label: 'Awaiting Data' },
        ]} testId="ladder-rating-filter" />
        <p className="text-[10px] text-[#A3A3A3] ml-auto">{filtered.length} of {data.rows.length} rows</p>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] overflow-x-auto">
        <table className="w-full" data-testid="ladder-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5] sticky top-0 z-10">
            <tr>
              {SORT_KEYS.map(col => (
                <th key={col.key} className="text-left py-3 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373] whitespace-nowrap">
                  <button onClick={() => toggleSort(col.key)} className="inline-flex items-center gap-1 hover:text-[#171717]">
                    {col.label}
                    <ArrowUpDown className={`w-2.5 h-2.5 ${sortKey === col.key ? 'text-[#171717]' : 'text-[#D4D4D4]'}`} strokeWidth={2} />
                  </button>
                </th>
              ))}
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr
                key={`${r.subjectType}-${r.subject}`}
                data-testid={`ladder-row-${r.subjectType.toLowerCase().replace(/\s+/g, '-')}-${r.subject.replace(/\s+/g, '-')}`}
                onClick={() => openDeepDive(r)}
                className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA] cursor-pointer transition-colors"
              >
                <td className="py-2.5 px-3"><RatingPill ratingKey={r.overall.ratingKey || 'pending'} size="xs" /></td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.subjectType === 'Team Leader'
                      ? <Crown className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" strokeWidth={1.5} />
                      : <User className="w-3.5 h-3.5 text-[#A3A3A3] flex-shrink-0" strokeWidth={1.5} />}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#171717] truncate">{r.subject}</p>
                      {r.teamLeader && <p className="text-[10px] text-[#A3A3A3] truncate">reports to {r.teamLeader}</p>}
                      {r.managerCount != null && <p className="text-[10px] text-[#A3A3A3]">{r.managerCount} direct reports</p>}
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-3 text-[11px] text-[#737373]">{r.subjectType}</td>
                <NumCell value={r.planCount} />
                <NumCell value={r.totalLots?.toLocaleString()} />
                <NumCell value={fmtCompact(r.totalRevenue)} bold />
                <RatedCell value={r.pctUnderContract} suffix="%" pillarKey="agreement" pillars={r.pillars} />
                <NumCell value={fmtCompact(r.revenueAtRisk)} colorize="risk" />
                <RatedCell value={r.avgComplianceScore} suffix="/100" pillarKey="compliance" pillars={r.pillars} />
                <RatedCell value={r.ratedSevenPlusPct} suffix="%" pillarKey="nps" pillars={r.pillars} />
                <RatedCell value={r.enps} prefix={r.enps > 0 ? '+' : ''} pillarKey="enps" pillars={r.pillars} />
                <td className="py-2.5 px-3 whitespace-nowrap">
                  {r.workloadBandKey
                    ? <WorkloadChip bandKey={r.workloadBandKey} pct={r.trackedVsTeamAvgPct} hoursPerWeek={r.workloadHoursForBand ?? r.capacity?.totalHoursWeekly} />
                    : <span className="text-[#A3A3A3]">—</span>}
                </td>
                <td className="py-2.5 px-3 whitespace-nowrap"><EverperformPill ep={r.everperform} /></td>
                <td className="py-2.5 px-2 text-right">
                  <ArrowRight className="w-3.5 h-3.5 text-[#A3A3A3] group-hover:text-[#171717]" strokeWidth={1.5} />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={14} className="py-12 text-center text-sm text-[#A3A3A3]">
                  No rows match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────
const SummaryChip = ({ label, value, keyName }) => {
  const s = RATING_STYLE[keyName];
  return (
    <div className={`px-3 py-1.5 rounded-[8px] border ${s.border} bg-white/95`}>
      <p className="text-[9px] uppercase tracking-wider font-semibold text-[#737373]">{label}</p>
      <p className={`text-lg font-bold ${s.text} font-heading`}>{value ?? 0}</p>
    </div>
  );
};

const Selector = ({ label, value, onChange, options, testId }) => (
  <div className="flex items-center gap-2">
    <Filter className="w-3 h-3 text-[#A3A3A3]" strokeWidth={2} />
    <span className="text-[10px] uppercase tracking-wider font-semibold text-[#737373]">{label}</span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1.5 text-xs rounded-[6px] border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#171717]"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const NumCell = ({ value, bold, colorize }) => {
  let cls = 'text-[#525252]';
  if (bold) cls = 'text-[#171717] font-semibold';
  if (colorize === 'risk' && typeof value === 'string' && value !== '—' && value !== '$0') cls = 'text-red-700 font-semibold';
  return <td className={`py-2.5 px-3 text-sm text-right whitespace-nowrap ${cls}`}>{value ?? '—'}</td>;
};

// ── Everperform pulse pill ────────────────────────────────────────────────
// Renders the composite Wellbeing/Compliance/Performance pulse score with a
// coloured background band and a variance arrow.  Falls back to a small
// placeholder when the person has no pulse (rather than blanking the cell).
const EverperformPill = ({ ep }) => {
  if (!ep || !ep.available || ep.compositeScore == null) {
    return <span className="text-[#A3A3A3] text-xs">—</span>;
  }
  const score = ep.compositeScore;
  const bg = score >= 85 ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
           : score >= 70 ? 'bg-lime-50 text-lime-800 border-lime-300'
           : score >= 55 ? 'bg-amber-50 text-amber-900 border-amber-300'
           :               'bg-rose-50 text-rose-800 border-rose-300';
  const varr = ep.performance?.variance ?? ep.leadingIndicator?.var ?? 0;
  const arrow = varr > 0.5 ? '↑' : varr < -0.5 ? '↓' : '·';
  const arrowColor = varr > 0.5 ? 'text-emerald-600' : varr < -0.5 ? 'text-rose-600' : 'text-[#A3A3A3]';
  return (
    <span
      title={`Wellbeing ${ep.wellbeing?.score ?? '—'} · Compliance ${ep.compliance?.score ?? '—'} · Performance ${ep.performance?.score ?? '—'}${ep.membersCovered != null ? ` (avg across ${ep.membersCovered}/${ep.memberCount} members)` : ''}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${bg}`}
    >
      {Math.round(score)}
      <span className={`text-[10px] ${arrowColor}`}>{arrow}</span>
    </span>
  );
};

const RatedCell = ({ value, prefix = '', suffix = '', pillarKey, pillars }) => {
  if (value == null) return <td className="py-2.5 px-3 text-sm text-right text-[#A3A3A3]">—</td>;
  const pillar = pillars?.find(p => p.key === pillarKey);
  const ratingKey = pillar?.ratingKey || 'pending';
  const s = RATING_STYLE[ratingKey];
  return (
    <td className="py-2.5 px-3 text-sm text-right whitespace-nowrap">
      <span className={`inline-flex items-center justify-end gap-1.5 px-2 py-0.5 rounded-[5px] border ${s.border} ${s.bg}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        <span className={`font-semibold ${s.text}`}>{prefix}{typeof value === 'number' ? Math.round(value) : value}{suffix}</span>
      </span>
    </td>
  );
};

export default PerformanceLadder;
